/** RFC 0264 — the session VCS write model (code pointer, session branch). */
import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { normalizeDocumentPath } from '../documents/mutations'
import { changeProject, projectString } from '../project/scalars'
import type { ProjectDoc } from '../project/types'
import type { SessionDoc } from '../session/types'
import {
  DEFAULT_SOURCE_ID,
  ensureProjectSources,
  hasInferredDefaultSource,
  projectSources,
} from './sources'
import type { CodeRef, SessionCheckout, SessionCodeRef, VcsDoc } from './types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Draft = any

/**
 * Adopt a git repository as this project's code and install the vcs capability
 * (RFC 0264 &sect;4/&sect;6). Two writes, mirroring `registerApp`: create the
 * capability's own `VcsDoc` (holding the `CodeRef`), then register a thin
 * `{ vcs }` entry on the project's `capabilities`. The pointer's DATA lives in
 * the VcsDoc; only a registry entry lands on the core project doc, so a
 * docs-only project stays code-free. `trunk` is the integration branch (the repo
 * default or an explicit override), never the currently-checked-out branch. No
 * bytes move; the tree stays in git. Returns the VcsDoc id.
 */
export async function adoptCode(
  repo: Repo,
  projectId: string,
  opts: {
    vcs?: 'git' | 'jj'
    remote?: string
    trunk: string
    headCommit: string
    treeBlob?: { hash: string }
    sourceId?: string
    name?: string
    folder?: string
    legacyDefaultKind?: 'git' | 'folder'
  },
): Promise<string> {
  const sourceId = opts.sourceId ?? DEFAULT_SOURCE_ID
  const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
  const current = project.doc()
  if (opts.folder) {
    if (normalizeDocumentPath(opts.folder) !== opts.folder)
      throw new Error('invalid source folder')
    for (const ref of Object.values(current.documents ?? {})) {
      const path = normalizeDocumentPath(String(ref.path))
      if (
        path === opts.folder ||
        path?.startsWith(`${opts.folder}/`) ||
        opts.folder.startsWith(`${path}/`)
      )
        throw new Error(`${opts.folder} already holds a document`)
    }
    for (const source of projectSources(current).sources) {
      if (source.id === sourceId || !source.folder) continue
      if (
        source.folder === opts.folder ||
        source.folder.startsWith(`${opts.folder}/`) ||
        opts.folder.startsWith(`${source.folder}/`)
      )
        throw new Error(`${opts.folder} overlaps source ${source.name}`)
    }
  }
  if (
    !hasInferredDefaultSource(current) &&
    projectSources(current).sources.some((s) => s.id === sourceId)
  )
    throw new Error(`source ${sourceId} already exists`)
  const vcsDoc = repo.create<VcsDoc>()
  const vcsDocId = vcsDoc.documentId as string
  vcsDoc.change((d) => {
    // Assign only defined fields (Automerge throws on `undefined`).
    const code: CodeRef = {
      vcs: opts.vcs ?? 'git',
      trunk: opts.trunk,
      headCommit: opts.headCommit,
    }
    if (opts.remote !== undefined) code.remote = opts.remote
    if (opts.treeBlob !== undefined) code.treeBlob = opts.treeBlob
    d.code = code
  })
  changeProject(project, (d: Draft) => {
    ensureProjectSources(d, opts.legacyDefaultKind)
    if (!d.capabilities) d.capabilities = {}
    d.sources[sourceId] = {
      id: sourceId,
      name:
        opts.name ??
        (sourceId === DEFAULT_SOURCE_ID ? 'Project files' : sourceId),
      kind: 'git',
      vcsDocId,
      ...(opts.remote ? { remote: opts.remote } : {}),
      ...(opts.folder ? { folder: opts.folder } : {}),
    }
    if (!d.defaultSourceId) d.defaultSourceId = sourceId
    // Legacy readers continue to use the default source's VCS capability.
    if (sourceId === projectString(d.defaultSourceId))
      d.capabilities.vcs = { docId: vcsDocId }
  })
  return vcsDocId
}

/**
 * Advance the project code to a new head commit (RFC 0264). Resolves the vcs
 * capability's `VcsDoc` through the registry and moves its head. No-op if vcs is
 * not installed. The bytes reached git some other way (a push, a merge); this
 * moves the synced pointer.
 */
export async function advanceCode(
  repo: Repo,
  projectId: string,
  headCommit: string,
): Promise<void> {
  const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
  await (project as { whenReady?: () => Promise<void> }).whenReady?.()
  const docId = projectString(project.doc()?.capabilities?.vcs?.docId)
  if (!docId) return
  const vcsDoc = await repo.find<VcsDoc>(docId as AnyDocumentId)
  vcsDoc.change((d) => {
    if (d.code) d.code.headCommit = headCommit
  })
}

/**
 * Record the branch a session works on (RFC 0264 &sect;2). A session is a branch
 * of the project trunk (a fork or an attached branch); this stamps which one and
 * where its head sits, so a review reads a pointer, not a tree. Whole-value write
 * (the ref advances as a unit).
 */
export async function setSessionCode(
  repo: Repo,
  sessionId: string,
  code: SessionCodeRef,
): Promise<void> {
  const session = await repo.find<SessionDoc>(sessionId as AnyDocumentId)
  session.change((d) => {
    d.code = { ...code }
    // The newer write wins (RFC 0362): a per-source checkout for the same
    // source would otherwise outrank this `code` in `sessionCheckouts`, and a
    // CLI that writes `code` alone would be ignored.
    const sourceId =
      code.sourceId ?? (d.codeSourceId != null ? String(d.codeSourceId) : '')
    if (!sourceId || !d.checkouts) return
    for (const projectId of Object.keys(d.checkouts)) {
      const sources = d.checkouts[projectId]
      if (sources?.[sourceId]) delete sources[sourceId]
      if (sources && Object.keys(sources).length === 0)
        delete d.checkouts[projectId]
    }
  })
}

/**
 * Branch one source for a session, or return it to its shared checkout with
 * `null` (RFC 0362 3b). Only that source changes: the session's other sources
 * stay on whatever checkout they had.
 */
export async function setSessionCheckout(
  repo: Repo,
  sessionId: string,
  projectId: string,
  sourceId: string,
  checkout: SessionCheckout | null,
): Promise<void> {
  const session = await repo.find<SessionDoc>(sessionId as AnyDocumentId)
  await (session as { whenReady?: () => Promise<void> }).whenReady?.()
  session.change((d) => {
    if (checkout) {
      if (!d.checkouts) d.checkouts = {}
      if (!d.checkouts[projectId]) d.checkouts[projectId] = {}
      d.checkouts[projectId][sourceId] = { ...checkout }
      return
    }
    const sources = d.checkouts?.[projectId]
    if (sources?.[sourceId]) delete sources[sourceId]
    if (sources && Object.keys(sources).length === 0)
      delete d.checkouts?.[projectId]
  })
}

export async function setSessionSource(
  repo: Repo,
  sessionId: string,
  sourceId: string,
): Promise<void> {
  const session = await repo.find<SessionDoc>(sessionId as AnyDocumentId)
  session.change((d) => {
    d.codeSourceId = sourceId
  })
}

/**
 * Mirror a session's branch into its project's `sessionMeta` (RFC 0277 S12),
 * so a session row can show it with zero session hydration. A no-op when the
 * mirror already says so, which lets a reader call it whenever it sees the two
 * disagree without writing on every render.
 */
export async function noteSessionBranch(
  repo: Repo,
  projectId: string,
  sessionId: string,
  branch: string,
): Promise<void> {
  const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
  if (projectString(project.doc()?.sessionMeta?.[sessionId]?.branch) === branch)
    return
  changeProject(project, (d: Draft) => {
    if (!d.sessionMeta) d.sessionMeta = {}
    const existing = d.sessionMeta[sessionId]
    if (existing) existing.branch = branch
    else d.sessionMeta[sessionId] = { branch }
  })
}
